/**
 * LandingBlocks — the block components used by both the public renderer and
 * the CRM editor preview. Every block takes ({ props, theme }) and renders a
 * responsive, animated section themed from the design's theme object.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { resolveFontFamily } from '../../lib/fonts';
import {
  Palette, Cake, Sparkles, Gift, Heart, PartyPopper, Cookie, IceCream,
  Star, ShieldCheck, Users, Clock, CheckCircle2, Phone, Mail, MapPin,
  ArrowRight, Brush, Music, Camera,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
export const resolveMediaUrl = (u) => {
  if (!u) return '';
  if (/^https?:\/\//.test(u)) return u;
  if (u.startsWith('/api/')) return `${API_BASE}${u}`;
  return u;
};

const ICONS = {
  Palette, Cake, Sparkles, Gift, Heart, PartyPopper, Cookie, IceCream,
  Star, ShieldCheck, Users, Clock, CheckCircle2, Brush, Music, Camera,
};
const Icon = ({ name, className, style }) => {
  const C = ICONS[name];
  if (C) return <C className={className} style={style} />;
  // treat as emoji / short text
  return <span className={className} style={{ ...style, fontSize: '1.5em', lineHeight: 1 }}>{name || '✨'}</span>;
};

const hf = (t) => resolveFontFamily(t?.heading_font, '"Fredoka", sans-serif');
const bf = (t) => resolveFontFamily(t?.body_font, '"Poppins", sans-serif');
const isDark = (t) => {
  const c = (t?.bg || '#ffffff').replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) < 130;
};

const reveal = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const CTA = ({ href, children, kind = 'primary', theme, big }) => {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700,
    padding: big ? '16px 34px' : '13px 26px', fontSize: big ? 18 : 15,
    borderRadius: 999, textDecoration: 'none', cursor: 'pointer',
    transition: 'transform .18s ease, box-shadow .18s ease, background-color .18s ease',
    fontFamily: bf(theme),
  };
  const styles = kind === 'primary'
    ? { ...base, background: theme.primary, color: '#fff', boxShadow: `0 10px 28px -8px ${theme.primary}` }
    : { ...base, background: 'transparent', color: theme.primary, border: `2px solid ${theme.primary}` };
  const cls = 'lp-cta hover:-translate-y-0.5 hover:shadow-lg';
  const internal = href && href.startsWith('/') && !href.includes('#');
  if (internal) return <Link to={href} className={cls} style={styles}>{children}</Link>;
  return <a href={href || '#'} className={cls} style={styles}>{children}</a>;
};

const resolveLink = (link) => {
  if (!link || typeof link !== 'object' || !link.type || link.type === 'none' || !link.target) return null;
  let href = '';
  if (link.type === 'experience') href = `/unique-experiences/${link.target}`;
  else if (link.type === 'package') href = `/book?package=${encodeURIComponent(link.target)}`;
  else href = link.target;
  const external = /^https?:\/\//i.test(href);
  return { href, external, text: link.text || 'Learn more' };
};

const LinkCTA = ({ link, theme, className = '' }) => {
  const r = resolveLink(link);
  if (!r) return null;
  const style = { color: theme.primary, fontWeight: 700, fontFamily: bf(theme) };
  const cls = `inline-flex items-center gap-1 hover:gap-2 transition-all ${className}`;
  const inner = <>{r.text}<ArrowRight className="w-4 h-4" /></>;
  if (r.external) return <a href={r.href} target="_blank" rel="noopener noreferrer" className={cls} style={style} data-testid="card-link">{inner}</a>;
  return <Link to={r.href} className={cls} style={style} data-testid="card-link">{inner}</Link>;
};

const Section = ({ children, theme, style, className = '', ...rest }) => (
  <section
    className={`px-5 sm:px-8 lg:px-12 py-16 sm:py-24 ${className}`}
    style={{ fontFamily: bf(theme), color: theme.text, ...style }}
    {...rest}
  >
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const Eyebrow = ({ children, theme }) => children ? (
  <span className="inline-block text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3"
    style={{ color: theme.primary }}>{children}</span>
) : null;

const Title = ({ children, theme, className = '' }) => (
  <h2 className={`font-extrabold leading-tight ${className}`}
    style={{ fontFamily: hf(theme), color: theme.text }}>{children}</h2>
);

// ── HERO ──────────────────────────────────────────────────────────────────────
const HeroBlock = ({ props: p, theme }) => {
  const img = resolveMediaUrl(p.image);
  const dark = p.bg_style === 'image' || isDark(theme);
  const center = p.align === 'center';
  const textColor = p.bg_style === 'image' ? '#fff' : theme.text;
  const bg = p.bg_style === 'gradient'
    ? { background: `radial-gradient(1200px 500px at 15% -10%, ${theme.accent}55, transparent), radial-gradient(900px 500px at 110% 10%, ${theme.secondary}44, transparent), ${theme.bg}` }
    : p.bg_style === 'solid' ? { background: theme.bg } : {};
  const showSideImage = (p.bg_style === 'gradient' || p.bg_style === 'solid' || p.bg_style === 'split') && img;

  return (
    <section className="relative overflow-hidden" style={{ ...bg, color: textColor, fontFamily: bf(theme) }}>
      {p.bg_style === 'image' && img && (
        <>
          <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,.35), rgba(0,0,0,.62))' }} />
        </>
      )}
      <div className={`relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28 grid gap-10 items-center ${showSideImage && !center ? 'lg:grid-cols-2' : ''}`}>
        <motion.div {...reveal} className={center ? 'text-center mx-auto max-w-3xl' : 'text-left'}>
          <Eyebrow theme={theme}>{p.eyebrow}</Eyebrow>
          <h1 className="font-extrabold leading-[1.05] text-4xl sm:text-5xl lg:text-6xl"
            style={{ fontFamily: hf(theme), color: p.bg_style === 'image' ? '#fff' : theme.text }}>{p.title}</h1>
          {p.subtitle && (
            <p className="mt-5 text-base sm:text-lg max-w-2xl"
              style={{ color: p.bg_style === 'image' ? 'rgba(255,255,255,.9)' : theme.muted, marginInline: center ? 'auto' : 0 }}>{p.subtitle}</p>
          )}
          <div className={`mt-8 flex flex-wrap gap-3 ${center ? 'justify-center' : ''}`}>
            {p.primary_label && <CTA href={p.primary_href} theme={theme} big>{p.primary_label}<ArrowRight className="w-5 h-5" /></CTA>}
            {p.secondary_label && <CTA href={p.secondary_href} theme={theme} kind="secondary" big>{p.secondary_label}</CTA>}
          </div>
        </motion.div>
        {showSideImage && !center && (
          <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className="relative">
            <img src={img} alt="" className="w-full aspect-[4/3] object-cover shadow-2xl"
              style={{ borderRadius: theme.radius }} />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full opacity-70 blur-2xl" style={{ background: theme.accent }} />
          </motion.div>
        )}
      </div>
    </section>
  );
};

// ── MARQUEE ─────────────────────────────────────────────────────────────────
const MarqueeBlock = ({ props: p, theme }) => {
  const items = p.items || [];
  const row = [...items, ...items, ...items];
  return (
    <div className="overflow-hidden py-4" style={{ background: theme.primary }}>
      <div className="lp-marquee flex gap-10 whitespace-nowrap" style={{ animation: 'lp-scroll 22s linear infinite' }}>
        {row.map((it, i) => (
          <span key={i} className="text-lg font-bold" style={{ color: '#fff', fontFamily: hf(theme) }}>{it.label}</span>
        ))}
      </div>
    </div>
  );
};

// ── STATS ──────────────────────────────────────────────────────────────────
const StatsBlock = ({ props: p, theme }) => (
  <Section theme={theme} style={{ background: theme.bg }}>
    {p.title && <Title theme={theme} className="text-3xl sm:text-4xl text-center mb-10">{p.title}</Title>}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
      {(p.items || []).map((s, i) => (
        <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.06 }}
          className="text-center p-6" style={{ background: theme.surface, borderRadius: theme.radius, border: `1px solid ${theme.primary}18` }}>
          <div className="text-3xl sm:text-4xl font-extrabold" style={{ color: theme.primary, fontFamily: hf(theme) }}>{s.value}</div>
          <div className="text-sm mt-1" style={{ color: theme.muted }}>{s.label}</div>
        </motion.div>
      ))}
    </div>
  </Section>
);

// ── FEATURES ─────────────────────────────────────────────────────────────────
const FeaturesBlock = ({ props: p, theme }) => {
  const cols = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[p.columns || 3];
  return (
    <Section theme={theme} style={{ background: theme.bg }}>
      <div className="text-center max-w-2xl mx-auto mb-12">
        <Title theme={theme} className="text-3xl sm:text-4xl">{p.title}</Title>
        {p.subtitle && <p className="mt-3 text-base sm:text-lg" style={{ color: theme.muted }}>{p.subtitle}</p>}
      </div>
      <div className={`grid grid-cols-1 ${cols} gap-5`}>
        {(p.items || []).map((f, i) => (
          <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.06 }}
            className="p-7 h-full" style={{ background: theme.surface, borderRadius: theme.radius, border: `1px solid ${theme.text}12` }}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: `${theme.primary}18`, color: theme.primary }}>
              <Icon name={f.icon} className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold" style={{ fontFamily: hf(theme), color: theme.text }}>{f.title}</h3>
            <p className="mt-2 text-sm" style={{ color: theme.muted }}>{f.text}</p>
            <LinkCTA link={f.link} theme={theme} className="mt-3 text-sm" />
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

// ── STEPS ────────────────────────────────────────────────────────────────────
const StepsBlock = ({ props: p, theme }) => (
  <Section theme={theme} style={{ background: theme.bg }}>
    <div className="text-center max-w-2xl mx-auto mb-12">
      <Title theme={theme} className="text-3xl sm:text-4xl">{p.title}</Title>
      {p.subtitle && <p className="mt-3 text-base sm:text-lg" style={{ color: theme.muted }}>{p.subtitle}</p>}
    </div>
    <div className="grid gap-6 md:grid-cols-3">
      {(p.items || []).map((s, i) => (
        <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} className="relative p-7"
          style={{ background: theme.surface, borderRadius: theme.radius, border: `1px solid ${theme.text}10` }}>
          <div className="w-11 h-11 rounded-full flex items-center justify-center font-extrabold text-lg mb-4"
            style={{ background: theme.primary, color: '#fff', fontFamily: hf(theme) }}>{i + 1}</div>
          <h3 className="text-lg font-bold" style={{ fontFamily: hf(theme), color: theme.text }}>{s.title}</h3>
          <p className="mt-2 text-sm" style={{ color: theme.muted }}>{s.text}</p>
        </motion.div>
      ))}
    </div>
  </Section>
);

// ── GALLERY ──────────────────────────────────────────────────────────────────
const GalleryBlock = ({ props: p, theme }) => {
  const imgs = (p.images || []).filter((x) => x.url);
  const wrap = p.layout === 'masonry' ? 'columns-2 md:columns-3 gap-4 [&>*]:mb-4'
    : p.layout === 'carousel' ? 'flex gap-4 overflow-x-auto pb-4 snap-x'
    : 'grid grid-cols-2 md:grid-cols-3 gap-4';
  return (
    <Section theme={theme} style={{ background: theme.bg }}>
      {(p.title || p.subtitle) && (
        <div className="text-center max-w-2xl mx-auto mb-10">
          {p.title && <Title theme={theme} className="text-3xl sm:text-4xl">{p.title}</Title>}
          {p.subtitle && <p className="mt-3" style={{ color: theme.muted }}>{p.subtitle}</p>}
        </div>
      )}
      <div className={wrap}>
        {imgs.map((im, i) => (
          <motion.figure key={i} {...reveal} transition={{ ...reveal.transition, delay: (i % 6) * 0.05 }}
            className={`relative group overflow-hidden ${p.layout === 'carousel' ? 'snap-start shrink-0 w-72' : ''} ${p.layout === 'masonry' ? 'break-inside-avoid' : ''}`}
            style={{ borderRadius: theme.radius }}>
            <img src={resolveMediaUrl(im.url)} alt={im.caption || ''}
              className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${p.layout === 'grid' || p.layout === 'carousel' ? 'aspect-[4/3]' : ''}`} />
            {im.caption && <figcaption className="absolute bottom-0 inset-x-0 p-3 text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(0deg, rgba(0,0,0,.6), transparent)' }}>{im.caption}</figcaption>}
          </motion.figure>
        ))}
      </div>
    </Section>
  );
};

// ── PACKAGES (live) ────────────────────────────────────────────────────────
const PackagesBlock = ({ props: p, theme }) => {
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => {
    let ok = true;
    fetch(`${API_BASE}/api/packages`).then((r) => r.ok ? r.json() : []).then((d) => {
      if (!ok) return;
      const arr = Array.isArray(d) ? d : (d.packages || []);
      setPkgs(arr.filter((x) => x.active !== false).slice(0, 4));
    }).catch(() => {});
    return () => { ok = false; };
  }, []);
  return (
    <Section theme={theme} style={{ background: theme.bg }} id="packages">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <Title theme={theme} className="text-3xl sm:text-4xl">{p.title}</Title>
        {p.subtitle && <p className="mt-3 text-base sm:text-lg" style={{ color: theme.muted }}>{p.subtitle}</p>}
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        {pkgs.map((pk, i) => (
          <motion.div key={pk.id || i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}
            className="flex flex-col p-7" style={{ background: theme.surface, borderRadius: theme.radius, border: i === 1 ? `2px solid ${theme.primary}` : `1px solid ${theme.text}12` }}>
            <h3 className="text-xl font-bold" style={{ fontFamily: hf(theme), color: theme.text }}>{pk.name}</h3>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-4xl font-extrabold" style={{ color: theme.primary, fontFamily: hf(theme) }}>${Math.round(pk.price)}</span>
              <span className="text-sm" style={{ color: theme.muted }}>/ up to {pk.guests} kids</span>
            </div>
            {pk.description && <p className="mt-3 text-sm" style={{ color: theme.muted }}>{pk.description}</p>}
            <ul className="mt-5 space-y-2 flex-1">
              {(pk.features || []).slice(0, 5).map((f, j) => (
                <li key={j} className="flex items-start gap-2 text-sm" style={{ color: theme.text }}>
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: theme.secondary }} />{f}
                </li>
              ))}
            </ul>
            <div className="mt-6"><CTA href="/book" theme={theme}>Book {pk.name}<ArrowRight className="w-4 h-4" /></CTA></div>
          </motion.div>
        ))}
      </div>
      {p.note && <p className="text-center mt-8 text-sm" style={{ color: theme.muted }}>{p.note}</p>}
    </Section>
  );
};

// ── TESTIMONIALS ──────────────────────────────────────────────────────────────
const TestimonialsBlock = ({ props: p, theme }) => (
  <Section theme={theme} style={{ background: theme.bg }}>
    <Title theme={theme} className="text-3xl sm:text-4xl text-center mb-12">{p.title}</Title>
    <div className="grid gap-6 md:grid-cols-3">
      {(p.items || []).map((t, i) => (
        <motion.blockquote key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.07 }}
          className="p-7 h-full flex flex-col" style={{ background: theme.surface, borderRadius: theme.radius, border: `1px solid ${theme.text}10` }}>
          <div className="flex gap-1 mb-3">
            {Array.from({ length: t.rating || 5 }).map((_, s) => <Star key={s} className="w-4 h-4 fill-current" style={{ color: theme.accent }} />)}
          </div>
          <p className="flex-1 text-base" style={{ color: theme.text }}>&ldquo;{t.quote}&rdquo;</p>
          <footer className="mt-5">
            <div className="font-bold" style={{ fontFamily: hf(theme), color: theme.text }}>{t.name}</div>
            <div className="text-xs" style={{ color: theme.muted }}>{t.detail}</div>
            <LinkCTA link={t.link} theme={theme} className="mt-2 text-sm" />
          </footer>
        </motion.blockquote>
      ))}
    </div>
  </Section>
);

// ── CTA BANNER ────────────────────────────────────────────────────────────────
const CtaBlock = ({ props: p, theme }) => (
  <section className="px-5 sm:px-8 lg:px-12 py-6">
    <motion.div {...reveal} className="max-w-6xl mx-auto text-center px-6 py-14 sm:py-20 relative overflow-hidden"
      style={{ borderRadius: theme.radius, background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}>
      <h2 className="text-3xl sm:text-4xl font-extrabold text-white" style={{ fontFamily: hf(theme) }}>{p.title}</h2>
      {p.subtitle && <p className="mt-3 text-white/90 max-w-xl mx-auto">{p.subtitle}</p>}
      <div className="mt-8 flex justify-center">
        <Link to={p.cta_href || '/book'} className="inline-flex items-center gap-2 font-bold px-8 py-4 rounded-full bg-white hover:-translate-y-0.5 transition-transform"
          style={{ color: theme.primary, fontFamily: bf(theme) }}>{p.cta_label}<ArrowRight className="w-5 h-5" /></Link>
      </div>
    </motion.div>
  </section>
);

// ── FAQ ──────────────────────────────────────────────────────────────────────
const FaqBlock = ({ props: p, theme }) => {
  const [open, setOpen] = useState(0);
  return (
    <Section theme={theme} style={{ background: theme.bg }}>
      <Title theme={theme} className="text-3xl sm:text-4xl text-center mb-10">{p.title}</Title>
      <div className="max-w-3xl mx-auto space-y-3">
        {(p.items || []).map((f, i) => (
          <div key={i} style={{ background: theme.surface, borderRadius: theme.radius, border: `1px solid ${theme.text}12` }}>
            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 text-left p-5">
              <span className="font-bold" style={{ fontFamily: hf(theme), color: theme.text }}>{f.q}</span>
              <span className="text-2xl leading-none shrink-0" style={{ color: theme.primary }}>{open === i ? '−' : '+'}</span>
            </button>
            {open === i && <div className="px-5 pb-5 text-sm" style={{ color: theme.muted }}>{f.a}</div>}
          </div>
        ))}
      </div>
    </Section>
  );
};

// ── ABOUT ────────────────────────────────────────────────────────────────────
const AboutBlock = ({ props: p, theme }) => {
  const img = resolveMediaUrl(p.image);
  const right = p.image_side !== 'left';
  return (
    <Section theme={theme} style={{ background: theme.bg }}>
      <div className={`grid gap-10 items-center ${img ? 'lg:grid-cols-2' : ''}`}>
        <motion.div {...reveal} className={right ? 'order-1' : 'order-1 lg:order-2'}>
          <Title theme={theme} className="text-3xl sm:text-4xl">{p.title}</Title>
          <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: theme.muted }}>{p.body}</p>
        </motion.div>
        {img && (
          <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className={right ? 'order-2' : 'order-2 lg:order-1'}>
            <img src={img} alt="" className="w-full aspect-[4/3] object-cover shadow-xl" style={{ borderRadius: theme.radius }} />
          </motion.div>
        )}
      </div>
    </Section>
  );
};

// ── LOGO STRIP / TRUST ──────────────────────────────────────────────────────
const LogoStripBlock = ({ props: p, theme }) => (
  <section className="px-5 sm:px-8 lg:px-12 py-10" style={{ background: theme.surface }}>
    <div className="max-w-6xl mx-auto">
      {p.title && <p className="text-center text-xs font-bold uppercase tracking-[0.2em] mb-6" style={{ color: theme.muted }}>{p.title}</p>}
      <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
        {(p.items || []).map((it, i) => (
          <span key={i} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full"
            style={{ background: `${theme.primary}12`, color: theme.text }}>
            <ShieldCheck className="w-4 h-4" style={{ color: theme.secondary }} />{it.label}
          </span>
        ))}
      </div>
    </div>
  </section>
);

// ── CONTACT ──────────────────────────────────────────────────────────────────
const ContactBlock = ({ props: p, theme }) => (
  <Section theme={theme} style={{ background: theme.bg }}>
    <div className="max-w-3xl mx-auto text-center">
      <Title theme={theme} className="text-3xl sm:text-4xl mb-8">{p.title}</Title>
      <div className="grid sm:grid-cols-2 gap-4 text-left">
        {[[Phone, p.phone], [Mail, p.email], [MapPin, p.address], [Clock, p.hours]].filter(([, v]) => v).map(([Ic, v], i) => (
          <div key={i} className="flex items-center gap-3 p-5" style={{ background: theme.surface, borderRadius: theme.radius, border: `1px solid ${theme.text}12` }}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${theme.primary}18`, color: theme.primary }}><Ic className="w-5 h-5" /></span>
            <span className="text-sm" style={{ color: theme.text }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

// ── RICH TEXT ─────────────────────────────────────────────────────────────────
const RichTextBlock = ({ props: p, theme }) => (
  <Section theme={theme} style={{ background: theme.bg }}>
    <div className={`max-w-3xl ${p.align === 'center' ? 'mx-auto text-center' : p.align === 'right' ? 'ml-auto text-right' : ''}`}>
      {p.title && <Title theme={theme} className="text-2xl sm:text-3xl mb-3">{p.title}</Title>}
      <p className="text-base sm:text-lg leading-relaxed whitespace-pre-line" style={{ color: theme.muted }}>{p.body}</p>
    </div>
  </Section>
);

export const BLOCK_REGISTRY = {
  hero: HeroBlock,
  marquee: MarqueeBlock,
  stats: StatsBlock,
  features: FeaturesBlock,
  steps: StepsBlock,
  gallery: GalleryBlock,
  packages: PackagesBlock,
  testimonials: TestimonialsBlock,
  cta_banner: CtaBlock,
  faq: FaqBlock,
  about: AboutBlock,
  logo_strip: LogoStripBlock,
  contact: ContactBlock,
  richtext: RichTextBlock,
};

export const BlockView = ({ block, theme }) => {
  const C = BLOCK_REGISTRY[block.type];
  if (!C) return null;
  return <C props={block.props || {}} theme={theme} />;
};
